import React, { useState } from "react";
import toast from "react-hot-toast";
import { 
  useUpdateFeedbackStatusMutation
} from "../../../../store/apis/helpCenter.api";
import { 
  Modal, 
  TextArea, 
  ModalFooter, 
  Select,
  Button,
  Badge
} from "../../../common";
import { Eye, Clock, CheckCircle2, AlertCircle, User, Mail, FileCode, MessageSquare, FileText } from "lucide-react";

interface TicketDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: any;
}

const TicketDetailModal: React.FC<TicketDetailModalProps> = ({ 
  isOpen, 
  onClose, 
  ticket 
}) => {
  const [updateStatus, { isLoading: isUpdating }] = useUpdateFeedbackStatusMutation();
  const [status, setStatus] = useState<string>(ticket.status || "OPEN");

  const handleUpdate = async () => {
    try {
      await updateStatus({ 
        id: ticket._id || ticket.id, 
        status
      }).unwrap();
      toast.success("Feedback status updated successfully");
      onClose();
    } catch (error) {
      console.error("Failed to update feedback status:", error);
      toast.error("Failed to update feedback status");
    }
  };

  const statusOptions = [
    { value: "OPEN" as any, label: "Open" },
    { value: "CLOSED" as any, label: "Closed" },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <span>Feedback Support</span>
        </div>
      }
      size="4xl"
    >
      <div className="space-y-6 py-2">
        {/* Ticket Header Info */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 p-5 bg-slate-50 rounded-xl border border-slate-100">
          <div className="space-y-1">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Employee Name</span>
            <div className="flex items-center gap-2 text-slate-700">
              <User className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-semibold">{ticket.name || 'Anonymous'}</span>
            </div>
          </div>

          <div className="space-y-1">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Employee Email</span>
            <div className="flex items-center gap-2 text-slate-700">
              <Mail className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-semibold">{ticket.email || 'N/A'}</span>
            </div>
          </div>

          <div className="space-y-1">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Module Category</span>
            <div className="flex items-center gap-2">
              <FileCode className="w-4 h-4 text-slate-400" />
              <Badge variant="blue" size="middle">
                {ticket.moduleCode || 'General'}
              </Badge>
            </div>
          </div>

          <div className="space-y-1">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Related Article</span>
            <div className="flex items-center gap-2 text-slate-700">
              <FileText className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-semibold truncate max-w-[200px]">
                {ticket.articleTitle || 'None'}
              </span>
            </div>
          </div>

          <div className="space-y-1">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Submitted On</span>
            <div className="flex items-center gap-2 text-slate-700">
              <Clock className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-semibold">
                {ticket.createdAt 
                  ? (() => {
                      const date = new Date(ticket.createdAt);
                      const d = String(date.getDate()).padStart(2, '0');
                      const m = String(date.getMonth() + 1).padStart(2, '0');
                      const y = date.getFullYear();
                      const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                      return `${d}/${m}/${y}, ${time}`;
                    })()
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Query Content */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-slate-400" />
            <label className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Issue Description</label>
          </div>
          <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl text-slate-700 text-sm leading-relaxed">
            {ticket.description}
          </div>
        </div>

        {/* Management Controls */}
        <div className="pt-4 border-t border-slate-100 space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-slate-400" />
            <label className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Current Status</label>
          </div>
          <Select
            value={status}
            onChange={(val: any) => setStatus(val)}
            options={statusOptions}
            required
            className="max-w-xs"
          />
        </div>
      </div>
      <ModalFooter>
        <Button appearance="secondary" onClick={onClose} disabled={isUpdating}>
          Close
        </Button>
        <Button 
          appearance="primary" 
          onClick={handleUpdate} 
          isLoading={isUpdating}
          >
          Update Status
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TicketDetailModal;
